import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react"
import Text from "lib/components/Text"
import { LanguageContext } from "context/LanguageContext"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { CreateCrmClientVariables } from "@graphql/crm/create_crm_client"
import Script from "next/script"
import useSWR from "swr"
import fetcher from "lib/fetcher"
import { debounce } from "lodash"
import Input from "lib/components/Input"
import isDisposableEmail from "lib/disposable-email-domains"
import Select from "lib/components/Select"
import Button from "lib/components/Button"
import Radio from "lib/components/Radio"
import TextArea from "lib/components/TextArea"
import { toast } from "react-toastify"
import { gtmEvent } from "lib/gtm"
import { useRouter } from "next/router"
import { isDesktop } from "react-device-detect"
import Link from "next/link"
import ImageComponent from "lib/components/Image"
import UTMTracker from "@components/shared-components/UtmTracker"
import { FORM_LEADS_BG, getTrackingData } from "constants/common"
import { isValidName, optimizeImageCloudinary, sanitizeName, sanitizeText } from "lib/helpers"
import useRecaptcha from "hooks/useRecaptcha"
import dynamic from "next/dynamic"

// Client-only: react-phone-input-2's 45 KB stylesheet sits at InputPhone's module scope, so a
// static import keeps it render-blocking. h-16 matches FormField's height (label 20 + mb-2 8 +
// fieldBox h-9 36) to hold the grid row on swap.
const InputPhone = dynamic(() => import("lib/components/InputPhone"), {
  ssr: false,
  loading: () => <div className="h-16" />,
})

const BusinessForm: FC = () => {
  const router = useRouter()
  const { useTranslation: t, language } = useContext(LanguageContext)
  const methods = useForm<CreateCrmClientVariables>({
    shouldFocusError: true,
    mode: "onChange",
    reValidateMode: "onChange",
  })
  const [dataBusinessCategory, setDataBusinessCategory] = useState([])
  const [debouncedCategory, setDebouncedCategory] = useState<string | null>(null)
  const [activeStep, setActiveStep] = useState<number>(1)
  const [isRealDesktop, setIsRealDesktop] = useState<boolean>(false)

  const {
    ref: captchaRef,
    isPassed: isRecaptchaPassed,
    getToken: getCaptchaToken,
    reset: resetCaptcha,
  } = useRecaptcha({ enabled: activeStep === 2 })

  const {
    control,
    formState: { errors },
    register,
    watch,
    setValue,
    trigger,
    handleSubmit,
    clearErrors,
  } = methods

  const [loading, setLoading] = useState(false)

  const { data: businessCategoryList, isLoading } = useSWR(
    `/api/crm/get-business-category${debouncedCategory ? `?q=${debouncedCategory}` : ""}`,
    fetcher,
  )

  useEffect(() => {
    if (isDesktop) {
      setIsRealDesktop(true)
    }
  }, [])

  useMemo(() => {
    if (businessCategoryList?.data?.business_category) {
      setDataBusinessCategory(businessCategoryList.data.business_category)
    }

    return () => {
      setDataBusinessCategory([])
    }
  }, [businessCategoryList])

  const debounceSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedCategory(value)
      }, 500),
    [],
  )

  const handleInputChange = useCallback(
    (input: string) => {
      debounceSearch(input)
    },
    [debounceSearch],
  )

  const submitForm = async (data: CreateCrmClientVariables) => {
    setLoading(true)
    // Sanitize all user inputs to prevent XSS
    const dataPayload: CreateCrmClientVariables = {
      ...data,
      first_name: sanitizeName(data.first_name, 25),
      last_name: data.last_name ? sanitizeName(data.last_name, 25) : data.last_name,
      company_name: data.company_name ? sanitizeText(data.company_name, 200) : data.company_name,
      job_title: data.job_title ? sanitizeText(data.job_title, 200) : data.job_title,
      notes: data.notes ? sanitizeText(data.notes, 2000) : data.notes,
    }
    try {
      const trackingData = getTrackingData()

      const leadsId = await fetcher("/api/crm/create-crm-client", "POST", {
        ...dataPayload,
        utm_source: trackingData?.utm_source,
        campaign_id: trackingData?.campaign_id,
        campaign_name: trackingData?.utm_campaign,
        ad_group: trackingData?.utm_content,
        recaptcha_token: getCaptchaToken(),
      })
      setLoading(false)
      toast.success(t("we_already_received_your_message"))
      gtmEvent("ce_form_submission", {
        form_id: leadsId,
        form_name: "For Business",
        form_company_name: dataPayload.company_name,
        form_job_title: dataPayload.job_title,
        email: dataPayload.email,
      })
      methods.reset()

      setActiveStep(1)
      router.query.submitted = "true"
      setTimeout(() => {
        router.replace(router)
      }, 3000)
    } catch (error) {
      setLoading(false)
      let message = t("error_message")
      if (error.message === "ErrCompanyNameAlreadyExist") {
        message = t("error_submit_company")
      } else if (error.message === "Invalid input detected") {
        message = t("invalid_input_detected_hint")
      } else if (error.message === "ErrInvalidRecaptcha") {
        message = t("invalid_recaptcha")
      }
      toast.error(message)
      resetCaptcha()
      setActiveStep(1)
    }
  }

  const onNext = async () => {
    if (activeStep === 2) {
      return handleSubmit(submitForm)()
    }

    const isValid = await methods.trigger([
      "first_name",
      "phone",
      "email",
      "company_name",
      "job_title",
      "business_category",
    ])
    if (isValid) {
      return setActiveStep(activeStep + 1)
    }
    return false
  }

  const estimationOptions = [
    {
      label: `4 - 10 ${t("participants")}`,
      value: "4 - 10 Participants",
    },
    {
      label: `11 - 20 ${t("participants")}`,
      value: "11 - 20 Participants",
    },
    {
      label: `>20 ${t("participants")}`,
      value: ">20 Participants",
    },
    {
      label: `${t("open_for_discussion")}`,
      value: "Open for Discussion",
    },
  ]

  return (
    <>
      <style>
        {`
          .placeholder-muted::placeholder {
            color: #DDD; 
          }
          textarea::placeholder {
            color: #DDD; 
          }
          `}
      </style>
      <UTMTracker />
      {/* Loads only at step 2, the only step that renders the widget: keeps ~350 KB off every page load. */}
      {activeStep === 2 && (
        <Script
          src="https://www.google.com/recaptcha/api.js?render=explicit"
          strategy="afterInteractive"
        />
      )}
      {/* Background is a lazy image under `isolate` + `-z-10` (kept inside the section, below the
          card); bg-primary-hover covers hash-link landings until it arrives. The image's absolute
          wrapper span paints over non-positioned siblings, so the container below must stay
          `relative` and after the image in DOM order. */}
      <section className="relative isolate bg-primary-hover py-[52px]" id="form_leads_section">
        <ImageComponent
          src={optimizeImageCloudinary(FORM_LEADS_BG, "auto", "w_1600")}
          alt=""
          sizes="100vw"
          objectFit="cover"
          className="-z-10"
        />
        <div className="relative container mx-auto px-4 lg:px-0">
          <div className="bg-white rounded-2xl p-4 lg:py-6 lg:px-16 flex flex-col gap-4 lg:gap-10">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <Text weight="semibold" customClass="text-base lg:text-xl">
                  {t("fill_out_the_form_part1")}{" "}
                  <span className="text-danger">{t("free_trial_in_house")}</span>{" "}
                  {t("fill_out_the_form_part2")}
                </Text>
                <div className="flex flex-col">
                  <Text customClass="text-xs lg:text-lg">
                    {t("our_team_will_follow_up_to_help_you_with_your_training_needs")}
                  </Text>
                  <Text customClass="text-xs lg:text-sm italic">
                    {t("terms_and_conditions_apply")}
                  </Text>
                </div>
              </div>
              <hr
                className="border-t border-transparent"
                style={{
                  borderImage:
                    "repeating-linear-gradient(to right, #D1D5DB 0 6px, transparent 6px 12px)",
                  borderImageSlice: 1,
                }}
              />
              <FormProvider {...methods}>
                <form onSubmit={onNext}>
                  {activeStep === 1 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                      <Input
                        label={`${t("first_name")} *`}
                        type="text"
                        {...register("first_name", {
                          required: t("first_name_required"),
                          maxLength: {
                            value: 25,
                            message: t("first_name_max_length") || "First name is too long",
                          },
                          validate: (value) =>
                            isValidName(value, 25) || t("invalid_name_format") || "Invalid name format",
                        })}
                        placeholder={t("first_name")}
                        error={!!errors.first_name}
                        message={errors.first_name && errors.first_name.message.toString()}
                      />
                      <Input
                        {...register("last_name", {
                          maxLength: {
                            value: 25,
                            message: t("last_name_max_length") || "Last name is too long",
                          },
                          validate: (value) =>
                            !value || isValidName(value, 25) || t("invalid_name_format") || "Invalid name format",
                        })}
                        label={`${t("last_name")}`}
                        placeholder={t("last_name")}
                        type="text"
                        error={!!errors.last_name}
                        message={errors.last_name && errors.last_name.message?.toString()}
                      />
                      <Controller
                        control={control}
                        name="phone"
                        rules={{
                          required: t("phone_number_required"),
                          pattern: {
                            value: /^[0-9]{8,14}$/,
                            message: t("phone_number_must_match_rule"),
                          },
                        }}
                        render={({ field: { onChange, value } }) => (
                          <InputPhone
                            defaultCountry="id"
                            countryCodeEditable
                            enableSearch
                            disableSearchIcon
                            label={`${t("phone_number")} *`}
                            placeholder="821XXXXX"
                            error={!!errors.phone}
                            message={errors.phone && errors.phone.message}
                            value={value}
                            onChange={(event) => {
                              onChange(event)
                              trigger("phone")
                            }}
                          />
                        )}
                      />
                      <Input
                        type="email"
                        {...register("email", {
                          required: t("email_required"),
                          pattern: {
                            value: /^[A-Z0-9._-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: t("invalid_email_message"),
                          },
                          validate: (value) =>
                            value && isDisposableEmail(value) ? t("invalid_email_address") : true,
                        })}
                        error={!!errors.email}
                        message={errors.email && errors.email.message.toString()}
                        placeholder={t("work_email")}
                        label={`${t("work_email")} *`}
                      />
                      <Input
                        label={`${t("company_name")} *`}
                        placeholder={t("company_name")}
                        {...register("company_name", {
                          required: t("company_required"),
                        })}
                        type="text"
                        error={!!errors.company_name}
                        message={errors.company_name && errors.company_name.message.toString()}
                      />
                      <Input
                        label={`${t("job_title")} *`}
                        placeholder={t("job_title")}
                        type="text"
                        {...register("job_title", {
                          required: t("job_title_required"),
                        })}
                        error={!!errors?.job_title}
                        message={errors?.job_title?.message}
                      />
                      <Select
                        name="business_category"
                        options={dataBusinessCategory || []}
                        label={`${t("business_category")} *`}
                        placeholder={t("select_search_business_category")}
                        {...register("business_category_id", {
                          required: t("business_category_required"),
                        })}
                        value={dataBusinessCategory.filter(
                          (it) => it.value === watch("business_category_id"),
                        )}
                        onChange={(eventTarget) => {
                          setValue("business_category_id", eventTarget.value, { shouldDirty: true })
                          clearErrors("business_category_id")
                        }}
                        handleInputChange={handleInputChange}
                        isLoading={isLoading}
                        error={!!errors.business_category_id}
                        message={
                          errors.business_category_id &&
                          errors.business_category_id.message.toString()
                        }
                      />
                    </div>
                  )}
                  {activeStep === 2 && (
                    <div className="grid grid-cols-1 gap-4 lg:gap-6">
                      <Radio
                        {...register("participant_detail", {
                          required: t("estimated_participants_required"),
                        })}
                        isHorizontal={isRealDesktop}
                        isHorizontalChild
                        label={`${t("estimated_total_participants")} *`}
                        labelColor="dark"
                        size="medium"
                        options={estimationOptions}
                        name="participant_detail"
                        value={watch("participant_detail")}
                        onChange={(event) =>
                          setValue("participant_detail", event.target.value, { shouldDirty: true })
                        }
                        error={!!errors.participant_detail}
                        message={
                          errors.participant_detail && errors.participant_detail.message.toString()
                        }
                      />
                      <TextArea
                        rows={isRealDesktop ? 10 : 5}
                        label={`${t("what_are_training_topic_you_looking_for")} *`}
                        placeholder={t("input_the_training_detail_you_are_looking_for")}
                        {...register("notes", { required: t("notes_required") })}
                        error={!!errors?.notes}
                        message={errors?.notes?.message}
                      />
                      {activeStep === 2 && (
                        <div className="flex flex-col gap-4 items-start justify-start">
                          <Text customClass="text-xs lg:text-base">
                            {t("by_submitting_form")}
                            <span className="text-primary font-semibold">
                              <Link href="/privacy-policy">{` ${
                                language === "ID" ? t("privacy_policy") : `${t("privacy_policy")}.`
                              }`}</Link>
                            </span>{" "}
                            {`${language === "ID" ? "kami." : ""}`}
                          </Text>
                          {/* Reserves the v2 widget's 78 px: it mounts only once api.js loads at step 2. */}
                          <div className="min-h-[78px] hover:cursor-pointer" ref={captchaRef} />
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </FormProvider>
            </div>
            <div className="flex flex-row items-center justify-between gap-3">
              <div className="flex flex-row items-center gap-3">
                {activeStep > 1 && (
                  <Button
                    variant="outlined"
                    color="muted"
                    customClass="w-fit lg:h-12"
                    fontWeight="semibold"
                    colorText="gray"
                    onClick={() => setActiveStep(activeStep - 1)}>
                    {t("back")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onNext}
                  loading={loading}
                  type={activeStep === 2 ? "submit" : "button"}
                  disabled={activeStep === 2 && !isRecaptchaPassed}
                  customClass="!w-[101px] lg:h-12"
                  fontWeight="semibold">
                  {activeStep > 1 ? t("submit") : "Next"}
                </Button>
              </div>
              <div className="px-4 flex gap-2 place-content-center">
                {Array.from({
                  length: 2,
                }).map((_, indexDot) => {
                  let bgGray = "bg-neutral-40"
                  if (indexDot + 1 === activeStep) {
                    bgGray = "bg-primary"
                  }

                  return (
                    <div
                      key={indexDot + 1}
                      className={`${
                        indexDot + 1 === activeStep ? "w-4" : "w-2"
                      } h-2 ${bgGray} rounded-full cursor-pointer`}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default BusinessForm
